import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { useCart } from '../context/CartContext';
import { useVendor } from '../context/VendorContext';
import {
  getPositionFresh,
  cerrarVisitaSilent,
  registrarPuntoVisitaSilent,
} from './useVisitaTracking';
import { isNetworkError } from '../utils/offlinePedidoQueue';
import { enqueueGpsMutation, flushGpsQueue, newGpsId } from '../utils/offlineGpsQueue';
import { getGpsSnapshot } from '../utils/gpsStatusStore';
import { mysqlNow, toMysqlDatetime } from '../utils/mysqlDatetime';
import { assessGpsFix } from '../utils/gpsQuality';

function visitaPayload(s, vendedor, state, freshPos) {
  const fromFresh =
    freshPos?.lat != null && freshPos?.lng != null ? assessGpsFix(freshPos) : null;
  const gps = getGpsSnapshot();
  const fromSnap =
    gps?.status === 'ok' && gps.lat != null && gps.lng != null
      ? assessGpsFix({
          lat: gps.lat,
          lng: gps.lng,
          accuracy: gps.accuracy,
          client_ts: gps.updatedAt ? new Date(gps.updatedAt).toISOString() : null,
        })
      : null;
  const good = fromFresh?.ok ? fromFresh.pos : fromSnap?.ok ? fromSnap.pos : null;
  return {
    sucursal_id: s.id,
    sucursal_codigo: s.codigo,
    sucursal_nombre: s.nombre,
    vendedor_id: s.vendedor_id || vendedor?.id || state.cliente.vendedor_id || null,
    vendedor_nombre: s.vendedor_nombre || vendedor?.nombre || state.cliente.vendedor,
    lat: good?.lat ?? null,
    lng: good?.lng ?? null,
    accuracy: good?.accuracy ?? null,
    client_ts: good?.client_ts || undefined,
  };
}

function attachGps(visita) {
  if (!visita?.id) return;
  // Fix fresco (maximumAge 0) al abrir visita / empezar pedido.
  getPositionFresh().then((pos) => {
    if (pos.lat == null || pos.lng == null) return;
    registrarPuntoVisitaSilent(visita.id, pos);
  });
}

function isNetworkFailure(err) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  return isNetworkError(err);
}

function applyVisitaToCart(dispatch, visita, activeSelectionRef, row) {
  if (
    !visita?.id ||
    !activeSelectionRef.current ||
    activeSelectionRef.current.token !== row.selectionToken ||
    activeSelectionRef.current.sucursalId !== row.sucursalId
  ) {
    return;
  }
  dispatch({
    type: 'SET_CLIENTE',
    data: {
      visita_id: visita.id,
      visita_inicio: toMysqlDatetime(visita.inicio) || mysqlNow(),
      visita_fin: null,
    },
  });
  attachGps(visita);
}

/**
 * Selecciona sucursal e inicia visita SIN bloquear por GPS.
 * Si falla iniciar visita, reintenta en segundo plano (el vendedor no se bloquea).
 */
export function useSelectSucursal() {
  const { state, dispatch } = useCart();
  const { vendedor } = useVendor();
  const [selecting, setSelecting] = useState(false);
  const activeSelectionRef = useRef(null);

  useEffect(() => {
    const onIniciarSuccess = (visita, row) => {
      applyVisitaToCart(dispatch, visita, activeSelectionRef, row);
    };

    const sync = () => {
      flushGpsQueue({ onIniciarSuccess }).catch(() => {});
    };

    const onVisitaIniciada = (event) => {
      const { visita, row } = event.detail || {};
      if (visita && row) onIniciarSuccess(visita, row);
    };

    sync();
    window.addEventListener('online', sync);
    window.addEventListener('oh-visita-iniciada', onVisitaIniciada);
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('oh-visita-iniciada', onVisitaIniciada);
    };
  }, [dispatch]);

  const selectSucursal = useCallback(
    async (s) => {
      if (selecting) return false;
      setSelecting(true);
      try {
        if (
          state.cliente.sucursal_id === s.id &&
          state.cliente.visita_id &&
          !state.cliente.visita_fin
        ) {
          dispatch({ type: 'SELECT_SUCURSAL', sucursal: s });
          return true;
        }

        if (state.cliente.visita_id && !state.cliente.visita_fin) {
          cerrarVisitaSilent(state.cliente.visita_id);
        }

        const selectionToken = newGpsId('sel');
        activeSelectionRef.current = { token: selectionToken, sucursalId: s.id };

        // Fix fresco breve (no bloquear >2.5s). Si falla, snapshot bueno o null.
        const freshPos = await Promise.race([
          getPositionFresh(),
          new Promise((resolve) => {
            setTimeout(() => resolve({ lat: null, lng: null }), 2500);
          }),
        ]);
        const payload = visitaPayload(s, vendedor, state, freshPos);
        let visita = null;
        try {
          const res = await api.iniciarVisita(payload);
          visita = res.data;
          attachGps(visita);
        } catch (err) {
          visita = null;
          if (isNetworkFailure(err)) {
            enqueueGpsMutation('iniciar', {
              payload,
              selectionToken,
              sucursalId: s.id,
            });
          }
          // Reintentos silenciosos: admin necesita visita_id para GPS/demora
          const tryAgain = async (attempt) => {
            if (activeSelectionRef.current?.token !== selectionToken) return;
            try {
              const res = await api.iniciarVisita(payload);
              const v = res.data;
              if (!v?.id) return;
              if (activeSelectionRef.current?.token !== selectionToken) return;
              dispatch({
                type: 'SET_CLIENTE',
                data: {
                  visita_id: v.id,
                  visita_inicio: toMysqlDatetime(v.inicio) || mysqlNow(),
                  visita_fin: null,
                },
              });
              attachGps(v);
            } catch (retryErr) {
              if (isNetworkFailure(retryErr)) {
                enqueueGpsMutation('iniciar', {
                  payload,
                  selectionToken,
                  sucursalId: s.id,
                });
              }
              if (attempt < 3 && activeSelectionRef.current?.token === selectionToken) {
                setTimeout(() => tryAgain(attempt + 1), 1500 * attempt);
              }
            }
          };
          setTimeout(() => tryAgain(1), 800);
        }

        dispatch({
          type: 'SELECT_SUCURSAL',
          sucursal: s,
          visita: visita || { inicio: mysqlNow() },
        });
        return true;
      } finally {
        setSelecting(false);
      }
    },
    [selecting, state.cliente, dispatch, vendedor]
  );

  const clearCliente = useCallback(async () => {
    const visitaId = state.cliente.visita_id;
    activeSelectionRef.current = null;
    // UI al toque: el admin no debe seguir en “tomando pedido…”
    dispatch({ type: 'CLEAR_CLIENTE' });
    try {
      // Cierra TODAS las visitas abiertas de este vendedor (aunque el id local se haya perdido)
      await api.cancelarVisitaActiva(getGpsSnapshot()?.status === 'ok'
        ? { lat: getGpsSnapshot().lat, lng: getGpsSnapshot().lng }
        : {});
    } catch {
      if (visitaId) cerrarVisitaSilent(visitaId);
    }
  }, [state.cliente.visita_id, dispatch]);

  return {
    selectSucursal,
    clearCliente,
    selecting,
    selectedSucursalId: state.cliente.sucursal_id,
    selectedCliente: state.cliente,
  };
}
